import { Flex } from '@mantine/core'

import { ACCOUNT_TYPES } from '@/types/accounts.types'
import type { PlaidAccountSubtypes } from '@/types/plaid.types'

import { AccountType } from '@/modules/accounts/components/AccountType/AccounType'
import { AdvisorAccountsList } from '@/modules/advisors/users/pages/[id]/components/AdvisorAccountsList/AdvisorAccountsList'
import type { AdvisorAccountsShellProps } from '@/modules/advisors/users/pages/[id]/components/AdvisorAccountsShell/AdvisorAccountsShell.types'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { getCurrentBalanceType } from '@/helpers/getCurrentBalanceType.helper'

import classes from './AdvisorAccountsShell.module.css'

export function AdvisorAccountsShell({ accounts }: AdvisorAccountsShellProps) {
  const listedAccounts = new Set()

  const accountTypes = [...new Set(accounts.map(account => account.type))]
  const accountSubtypes = [...new Set(accounts.map(account => account.subtype))]

  const availableAccountTypes = ACCOUNT_TYPES.filter(
    accountType =>
      accountType.type.some(t => accountTypes.includes(t)) &&
      (accountType.subtype
        ? accountSubtypes.some(accountSubtype =>
            accountType?.subtype?.includes(
              accountSubtype as PlaidAccountSubtypes,
            ),
          )
        : true),
  )

  const getFilteredAccounts = (types: string[], subtypes?: string[]) => {
    const filteredAccounts = accounts.filter(
      account =>
        types.includes(account.type) &&
        (subtypes ? subtypes.includes(account.subtype) : true) &&
        !listedAccounts.has(account.plaidId),
    )

    filteredAccounts.forEach(account => {
      listedAccounts.add(account.plaidId)
    })

    return filteredAccounts
  }

  const getAccountsInfo = (types: string[], subtype?: string[]) => {
    const filteredAccounts = getFilteredAccounts(types, subtype)

    const balance = filteredAccounts.reduce(
      (acc, account) =>
        acc +
        account.balance[getCurrentBalanceType(account.type, account.subtype)],
      0,
    )

    const formattedBalance = formatMoney(
      balance,
      filteredAccounts[0]?.balance.currency,
    )

    return {
      filteredAccounts,
      formattedBalance,
    }
  }

  return (
    <Flex className={classes.container} direction="column">
      {availableAccountTypes.map(accountType => {
        const { filteredAccounts, formattedBalance } = getAccountsInfo(
          accountType.type,
          accountType.subtype,
        )

        if (filteredAccounts.length === 0) {
          return null
        }

        return (
          <Flex key={accountType.label} className={classes.row}>
            <AccountType
              accountType={accountType}
              formattedBalance={formattedBalance}
            />

            <AdvisorAccountsList accounts={filteredAccounts} />
          </Flex>
        )
      })}
    </Flex>
  )
}
